import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  ChipToggle,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { useFormulary } from '@/features/encounters/api';
import { useMe, useSession } from '@/features/auth/session-context';
import { usePrescriptionActions } from '@/features/orders/api';
import { itemLine } from '@/features/orders/format';
import { isApiError } from '@/lib/api';
import { CancelPrescriptionDialog } from './CancelPrescriptionDialog';
import { useLivePrescription } from './consult-utils';
import styles from './consult.module.css';

const DAYS = ['1', '3', '5', '7', '14', '30'];
const OTHER = '__other__';

/**
 * Prescription (Admin v2 #69, #71, #72): each line is checked against
 * allergies, current medicines, interactions and maximum doses. A blocked
 * line keeps "Sign prescription" disabled until it is removed.
 */
export function RxSection({
  encounterId,
  editable,
}: {
  encounterId: string;
  editable: boolean;
}) {
  const live = useLivePrescription(encounterId);
  const a = usePrescriptionActions(encounterId);
  const { data: formulary } = useFormulary(editable);
  const toast = useToast();
  const me = useMe();
  const { hasPermission } = useSession();
  const [cancelling, setCancelling] = useState(false);
  const [code, setCode] = useState('');
  const [custom, setCustom] = useState('');
  const [dose, setDose] = useState('');
  const [freq, setFreq] = useState('3 times a day');
  const [days, setDays] = useState('5');
  const [prn, setPrn] = useState(false);
  const item = formulary?.medicines.find((m) => m.code === code);
  const doseOptions = item?.doses ?? [
    '1 tablet',
    '2 tablets',
    '1 capsule',
    '5 ml',
    '10 ml',
  ];
  const perDose = Number(/^\d+/.exec(dose)?.[0] ?? 1);
  const perDay =
    {
      'Once a day': 1,
      'Twice a day': 2,
      '3 times a day': 3,
      '4 times a day': 4,
      'At night': 1,
    }[freq] ?? 1;
  const quantity = perDose * perDay * Number(days);
  const medicine = code === OTHER ? custom.trim() : (item?.name ?? '');
  const ready =
    editable && medicine.length >= 2 && dose && freq && quantity >= 1;

  async function add() {
    try {
      await a.addItem.mutateAsync({
        medicine,
        dose,
        frequency: prn ? `${freq} (when needed)` : freq,
        durationDays: Number(days),
        quantity,
        unit: item?.unit ?? undefined,
      });
      setCode('');
      setCustom('');
      setDose('');
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Not added',
        body: isApiError(err) ? err.message : undefined,
        error: err,
      });
    }
  }

  return (
    <section className={styles.section} aria-label="Prescription">
      <p className="eyebrow">Prescription</p>
      <p className={styles.sub}>
        Checked against allergies, current medicines, interactions and maximum
        doses.
      </p>
      {live && live.items.length > 0 && (
        <ul className={styles.rxList}>
          {live.items.map((i) => (
            <li
              key={i.id}
              className={
                i.check?.status === 'blocked' ? styles.rxBlocked : undefined
              }
            >
              <div className={styles.rxHead}>
                <span>
                  <strong>{i.medicine}</strong>
                  <br />
                  <span className={styles.sub}>{itemLine(i)}</span>
                </span>
                <span className={styles.rxBadges}>
                  {i.check?.status === 'blocked' && (
                    <Badge tone="danger">Blocked</Badge>
                  )}
                  {i.check?.status === 'warning' && (
                    <Badge tone="warning">Check</Badge>
                  )}
                  {live.status === 'draft' && editable && (
                    <button
                      type="button"
                      className={styles.iconButton}
                      aria-label={`Remove ${i.medicine}`}
                      onClick={() =>
                        a.removeItem
                          .mutateAsync({ id: live.id, itemId: i.id })
                          .catch(() => undefined)
                      }
                    >
                      <X size={14} />
                    </button>
                  )}
                </span>
              </div>
              {i.check?.messages.map((m) => (
                <p
                  key={m.text}
                  className={
                    m.level === 'blocked' ? styles.blockNote : styles.warnNote
                  }
                >
                  {m.text}
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}
      {live?.status === 'signed' && (
        <p className={styles.okNote}>
          Signed {live.number} · in the dispensary queue and the patient’s app.
        </p>
      )}
      {/* RL-1837: the prescriber, or the clinic manager, cancels it with a reason. */}
      {live?.status === 'signed' &&
        ((live.prescriber.id === me.id &&
          hasPermission('prescriptions:sign')) ||
          hasPermission('prescriptions:cancel')) && (
          <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
            Cancel prescription
          </Button>
        )}
      {cancelling && live && (
        <CancelPrescriptionDialog
          number={live.number}
          pending={a.cancel.isPending}
          onClose={() => setCancelling(false)}
          onCancel={(reason) =>
            void a.cancel
              .mutateAsync({ id: live.id, reason })
              .then(() => {
                toast.show({
                  tone: 'success',
                  title: 'Prescription cancelled',
                });
                setCancelling(false);
              })
              .catch((err: unknown) =>
                toast.show({
                  tone: 'error',
                  title: isApiError(err)
                    ? (err.problem?.title ?? 'Not cancelled')
                    : 'Not cancelled',
                  body: isApiError(err) ? err.message : undefined,
                  error: err,
                }),
              )
          }
        />
      )}
      {editable && live?.status !== 'signed' && (
        <div className={styles.section}>
          <Field label="Medicine">
            <Select
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                const m = formulary?.medicines.find(
                  (x) => x.code === e.target.value,
                );
                setDose(m?.doses[0] ?? '');
              }}
              placeholder="Choose a medicine"
              options={[
                ...(formulary?.medicines ?? []).map((m) => ({
                  value: m.code,
                  label: m.name,
                })),
                { value: OTHER, label: 'Other (type the name)' },
              ]}
            />
          </Field>
          {code === OTHER && (
            <Field label="Medicine name and strength">
              <Input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
              />
            </Field>
          )}
          <div className={styles.three}>
            <Field label="Each dose">
              <Select
                value={dose}
                onChange={(e) => setDose(e.target.value)}
                placeholder="Dose"
                options={doseOptions.map((d) => ({ value: d, label: d }))}
              />
            </Field>
            <Field label="How often">
              <Select
                value={freq}
                onChange={(e) => setFreq(e.target.value)}
                options={(formulary?.frequencies ?? ['3 times a day']).map(
                  (f) => ({ value: f, label: f }),
                )}
              />
            </Field>
            <Field label="For (days)">
              <Select
                value={days}
                onChange={(e) => setDays(e.target.value)}
                options={DAYS.map((d) => ({ value: d, label: d }))}
              />
            </Field>
          </div>
          {item?.maxDailyMg && (
            <p className={styles.sub}>Maximum {item.maxDailyMg} mg a day</p>
          )}
          <div className={styles.rowBetween}>
            <ChipToggle
              label="When needed"
              options={[{ value: 'prn', label: 'When needed' }]}
              selected={prn ? ['prn'] : []}
              onChange={(v) => setPrn(v.includes('prn'))}
            />
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={14} />}
              disabled={!ready}
              loading={a.addItem.isPending}
              onClick={() => void add()}
            >
              Add to prescription
            </Button>
          </div>
          <p className={styles.legalNote}>
            Signing attaches your MMC number, date and clinic address, and the
            patient’s name, IC and address (Poisons Act Reg. 21). The e-Rx then
            appears in the dispensary queue and the patient’s app.
          </p>
          <p className={styles.sub}>
            Staging demo formulary: dose and interaction checks need the
            clinic’s licensed drug database.
          </p>
        </div>
      )}
    </section>
  );
}
